import request from 'tools/request'

/**
 * Unified healthcare search — fans out to NPPES + LEIE + SAM.gov on the backend and returns
 * collapsed identity cards. Gated by ClientFeatureFlag.HEALTHCARE_SEARCH server-side; frontend
 * gate lives in AuthService.buildMenubarList + the route guard.
 *
 * payload: { name?, npi?, state?, dob?, includeInactive? } — at least one required.
 */
// applicationId, when given, records the search against that application for its search history.
const searchPath = (applicationId) =>
    applicationId ? `healthcare-search?applicationId=${encodeURIComponent(applicationId)}` : 'healthcare-search'

export const healthcareSearch = ({ payload, applicationId }) => async () => {
    const res = await request(searchPath(applicationId), 'POST', payload)
    return res
}

/** Demo-mode variant — the backend ignores the query and returns the Kris R. Kile fixture. */
export const healthcareSearchDemoMode = ({ payload, applicationId }) => async () =>
    await request(searchPath(applicationId), 'POST', payload, false, { 'x-demo-mode': true })

// Searches run from an application list under it; Quick Searches (no application) list at the client level.
const historyPath = (applicationId) =>
    applicationId ? `applications/${applicationId}/healthcare-search/searches` : 'healthcare-search/searches'

/** Recent searches (newest first): an application's, or Quick Search's when no applicationId.
 *  Demo searches list only in demo mode. */
export const getHealthcareSearchHistory = ({ applicationId, isDemoMode }) => async () =>
    await request(historyPath(applicationId), 'GET', null, false, { 'x-demo-mode': !!isDemoMode })

/** Reopen a past search's stored result — no re-run, no credit. */
export const getHealthcareSearchResult = ({ applicationId, searchId, isDemoMode }) => async () =>
    await request(`${historyPath(applicationId)}/${searchId}`, 'GET', null, false, { 'x-demo-mode': !!isDemoMode })

/**
 * Save a collapsed identity card to an application. Idempotent server-side —
 * (client, application, matched_key) is unique; re-saving refreshes the snapshot.
 */
export const saveHealthcareSearchRecord = ({ applicationId, payload }) => async () => {
    const res = await request(`applications/${applicationId}/healthcare-search/save`, 'POST', payload)
    return res
}

/** List every saved identity for an application (Saved records panel). */
export const getHealthcareSearchSavedRecords = ({ applicationId }) => async () => {
    const res = await request(`applications/${applicationId}/healthcare-search/records`, 'GET')
    return res
}

/** People and businesses added to the application's list, including removed ones (removed: true). */
export const getHealthcareSearchTargets = ({ applicationId }) => async () =>
    await request(`applications/${applicationId}/healthcare-search/targets`, 'GET')

/** Add a person or business to the list without searching. payload: { targetKey, firstName?, lastName?, businessName?, npi?, state?, dob? } */
export const addHealthcareSearchTarget = ({ applicationId, payload }) => async () =>
    await request(`applications/${applicationId}/healthcare-search/targets`, 'POST', payload)

/** Remove an added person or business from the list; its searches and saved records are kept. */
export const removeHealthcareSearchTarget = ({ applicationId, targetKey }) => async () =>
    await request(`applications/${applicationId}/healthcare-search/targets?targetKey=${encodeURIComponent(targetKey)}`, 'DELETE')

/** Remove a saved identity by its record UUID. */
export const deleteHealthcareSearchSavedRecord = ({ applicationId, recordId }) => async () => {
    const res = await request(`applications/${applicationId}/healthcare-search/records/${recordId}`, 'DELETE')
    return res
}
